import { Component, useState, useEffect, Suspense } from 'react';
import { reportClientError } from './utils/clientErrors';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Every stylesheet, in the order it had before pages loaded lazily — FIRST, so
// the cascade is unchanged (scripts/genEagerStyles.mjs explains why).
import './eagerStyles.generated.js';
import { applyThemeForPath } from './utils/theme';
import lazyWithReload, { warmLazyChunks, isChunkLoadError } from './utils/lazyWithReload';

// Pages load when first opened, not all at boot: the entry script is small, so
// the first screen appears sooner. Their CSS stays in the entry (above). Every
// page is warmed once the tab is idle, so a deploy mid-session cannot strand a
// click on a chunk that no longer exists. Keep the `lazyPage(() => import('…'))`
// shape — the styles generator reads it.
const PAGE_FACTORIES = [];
const lazyPage = (factory) => { PAGE_FACTORIES.push(factory); return lazyWithReload(factory); };
const ProjectListPage = lazyPage(() => import('./pages/ProjectListPage/ProjectListPage'));
const ProjectWorkspace = lazyPage(() => import('./pages/ProjectWorkspace'));
const ProjectWideWorkspace = lazyPage(() => import('./pages/ProjectWide/ProjectWideWorkspace'));
const ProjectClassesPage = lazyPage(() => import('./pages/Classes/ProjectClassesPage'));
const ProjectModelsPage = lazyPage(() => import('./pages/Models/ProjectModelsPage'));
const ProjectDetectionPage = lazyPage(() => import('./pages/Detection/ProjectDetectionPage'));
const ProjectSmartLinksPage = lazyPage(() => import('./pages/Links/ProjectSmartLinksPage'));
const ProjectRegionsPage = lazyPage(() => import('./pages/ProjectRegionsPage'));
const ProjectExtractPage = lazyPage(() => import('./pages/Extract/ProjectExtractPage'));
const ProjectDocPropsPage = lazyPage(() => import('./pages/Properties/ProjectDocPropsPage'));
const ProjectSymbolsPage = lazyPage(() => import('./pages/ProjectSymbolsPage'));
const TemplateEditorPage = lazyPage(() => import('./pages/TemplateEditor/TemplateEditorPage.jsx'));
// Lazy-loaded: the twin/Model engine is heavy AND is now also imported by the
// MainView Model panel. Loading it eagerly here pulled the whole twin engine
// into the main chunk and created a circular chunk init at boot (TDZ crash).
// Lazy keeps it out of the eager graph; both the /twin route and the Model
// panel pull it on demand from a shared async chunk.
// LEGACY (/twin). Do not add features or fix bugs in this page - the digital
// twin lives in components/MainView/ModelLayer + Views/TwinDocOverlay.jsx.
// It is routed only so existing twins stay reachable. See the banner at the
// top of ProjectVectorisationPage.jsx.
const ProjectVectorisationPage = lazyWithReload(() => import('./pages/Vectorisation/ProjectVectorisationPage'));
// Warm it at boot so a deploy mid-session can't strand the first click on a
// missing chunk (see warmLazyChunks).
warmLazyChunks([() => import('./pages/Vectorisation/ProjectVectorisationPage')]);
const ProjectPunchesPage = lazyPage(() => import('./pages/Punches/ProjectPunchesPage'));
import LoginPage from './pages/LoginPage/LoginPage';
import VerifyEmailPage from './pages/LoginPage/VerifyEmailPage';
import ConfirmEmailChangePage from './pages/LoginPage/ConfirmEmailChangePage';
import ResetPasswordPage from './pages/LoginPage/ResetPasswordPage';
import JoinWorkspacePage from './pages/JoinWorkspace/JoinWorkspacePage';
import { initPreferencesSync, isPreferencesHydrated, hydratedBeforeHere } from './utils/preferencesSync';
import { apiFetch } from './utils/apiFetch';
import { setAvatarPalette } from './utils/presenceClient';
import { isAuthFlagSet, getAuthToken, setAuth, isTokenExpired, clearAuth } from './utils/authStorage';
import useSessionTimeout from './hooks/useSessionTimeout';
import { stepUiZoom, resetUiZoom } from './utils/uiZoom';
import PendingApprovalOverlay from './components/PendingApprovalOverlay';
import TwoFactorGate from './components/TwoFactorGate';
import UpdateNotice from './components/UpdateNotice';
import SaveFailureNotice from './components/SaveFailureNotice';
import { NoticeHost } from './components/shared/Notice';
import PageLoading from './components/shared/PageLoading';
import ThemeDialogHost from './components/ThemeDialogHost';
import './App.css';
import { trackPageview } from './utils/siteTracker';

warmLazyChunks(PAGE_FACTORIES);

// Marketing-site handoff — when a user signs in on pidly.com.au and
// clicks "Open app," we land with `#authToken=<jwt>&user=<encoded>`.
// Consume that immediately on first load so the protected-route guard
// sees a valid token, then strip the hash so it doesn't sit in the
// address bar or leak into the Referer header.
if (typeof window !== 'undefined' && window.location.hash.includes('authToken=')) {
  try {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = params.get('authToken');
    if (token) {
      let user = null;
      const rawUser = params.get('user');
      if (rawUser) {
        try { user = JSON.parse(decodeURIComponent(rawUser)); } catch {}
      }
      setAuth({ token, user, remember: true });
    }
    // Scrub the hash from the URL so the token isn't visible after load.
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState(null, '', cleanUrl);
  } catch (err) {
    console.warn('[app] handoff token parse failed:', err);
  }
}

// Error boundary - catches component crashes and shows recovery UI
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App crashed:', error, errorInfo);
    // Sent to pidly with the route, build, browser and who was signed in;
    // it shows on the Admin page's Errors tab.
    reportClientError({ error, componentStack: errorInfo?.componentStack || null, source: 'boundary' });
  }

  render() {
    if (this.state.hasError) {
      // Calm, themed, and no stack trace. The message a crash reaches the user
      // with was "Something went wrong" over a raw exception string in red —
      // which tells them nothing they can act on, and reads as the app being
      // broken rather than this view being. The detail is in the console,
      // where whoever can use it will look.
      // A STALE TAB (a chunk the server no longer has, after a deploy) is
      // not a crash: say what happened and offer the reload that fixes it.
      const stale = isChunkLoadError(this.state.error);
      return (
        <div className="error-boundary">
          <div className="error-boundary-card">
            <h1>{stale ? 'pidly has been updated' : 'This page hit a problem'}</h1>
            <p>
              {stale
                ? 'A new version of pidly went live while this tab was open, so part of the old one could not load. Reload to carry on with the new version. Nothing you saved has been changed.'
                : 'Nothing you were working on has been changed. Reload the page to carry on. The details have already been sent to us, so if it keeps happening, tell us what you were doing and we will look into it.'}
            </p>
            <div className="error-boundary-actions">
              <button
                type="button"
                className="error-boundary-btn error-boundary-btn--primary"
                onClick={() => window.location.reload()}
              >Reload</button>
              <button
                type="button"
                className="error-boundary-btn"
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.href = '/';
                }}
              >Go to Home</button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Protected route wrapper - redirects to login if not authenticated
let _prefsSyncStarted = false;
// Local dev bypass: on localhost, auto-mark authenticated so we can work
// offline without the login flow. Never fires off-localhost, so Railway
// deploys still require a real sign-in.
const _isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
if (_isLocalhost && !isAuthFlagSet() && !getAuthToken()) {
  try {
    localStorage.setItem('authenticated', 'true');
    localStorage.setItem('authToken', 'local-dev');
  } catch {}
}
// How long first paint will wait for the account's preferences. Past
// this the page renders from local storage and the hydrate lands in the
// background (a reload then shows the account's copy) — a cold Railway
// backend must not turn into a blank screen.
const PREFS_HYDRATE_WAIT_MS = 6000;
// The admin's avatar palette, fetched ONCE per page load and waited for
// before first paint (below). userColor reads a module-level palette, and
// a re-render of this route does NOT re-render its children (the same
// `children` element bails out), so a palette that landed after the first
// paint never reached the avatars already drawn — memoised lists, cached
// markup and dialogs kept the old colours. Fetching first ends that.
let _palettePromise = null;
let _paletteReady = false;
// Only a SUCCESSFUL read is kept. ProtectedRoute's effect also runs on the
// render that redirects a signed-out visitor to /login, and that request
// answers 401: caching it meant a cold (incognito) session never asked again
// after signing in and drew every face from the default palette.
function loadPaletteOnce() {
  if (!_palettePromise) {
    if (!getAuthToken() || isTokenExpired()) return Promise.resolve();
    _palettePromise = apiFetch('/api/avatar-palette')
      .then(r => r.json())
      .then(d => {
        if (d?.success) { setAvatarPalette(d.colors); _paletteReady = true; }
        else _palettePromise = null;
      })
      .catch(() => { _palettePromise = null; });
  }
  return _palettePromise;
}
function ProtectedRoute({ children }) {
  const isAuthenticated = isAuthFlagSet();
  const hasToken = !!getAuthToken();
  // The page does not render until the account's preferences are in
  // localStorage. Before this gate, every page mounted FIRST — reading
  // whatever this browser last had (or nothing, on a new laptop) into
  // React state — and the server's copy arrived a moment later, landing
  // in localStorage where nothing re-read it. So a second laptop showed
  // the wrong theme, object display, link palette, presets and stamps
  // until a reload, and its mount-time saves could push those stale
  // values over the account. Waiting once at boot ends both.
  const [prefsReady, setPrefsReady] = useState(() => (isPreferencesHydrated() && _paletteReady) || hydratedBeforeHere());

  useEffect(() => {
    // This effect also runs on the render that redirects a signed-out
    // visitor to /login. Hydrating then is refused (401) and used to mark
    // the account as hydrated, so the theme and settings only appeared after
    // a second reload. Wait until there is a live sign-in.
    if (!getAuthToken() || isTokenExpired()) return undefined;
    if (!_prefsSyncStarted) _prefsSyncStarted = true;
    // Init preferences sync (idempotent) — hydrates localStorage from the
    // server and watches for changes to sync back — and the avatar palette,
    // together, so the first paint already has both. A browser that has
    // hydrated this account before is already showing: the sync still
    // starts, it just does not hold the page.
    const sync = Promise.all([
      initPreferencesSync().catch(() => {}),
      loadPaletteOnce(),
    ]);
    if (prefsReady) return undefined;
    let done = false;
    const finish = () => { if (!done) { done = true; setPrefsReady(true); } };
    sync.then(finish, finish);
    const t = setTimeout(finish, PREFS_HYDRATE_WAIT_MS);
    return () => clearTimeout(t);
  }, [prefsReady]);

  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/login" replace />;
  }

  // JWT expired client-side — wipe stale auth and bounce to /login so the
  // user re-enters credentials instead of seeing a UI that silently 401s.
  if (isTokenExpired()) {
    clearAuth();
    return <Navigate to="/login" replace />;
  }

  if (!prefsReady) return <PageLoading fullPage />;

  return children;
}

// Block iOS Safari's native page-level pinch-zoom gestures globally. These
// fire on 'gesturestart/change/end' and — unlike touchmove — aren't stopped
// by CSS touch-action or viewport meta. Without this, pinching on a phone
// zooms the whole pidly UI instead of the PDF canvas's own handler. Safe
// no-op on Android/desktop.
if (typeof document !== 'undefined') {
  const prevent = (e) => e.preventDefault();
  document.addEventListener('gesturestart', prevent, { passive: false });
  document.addEventListener('gesturechange', prevent, { passive: false });
  document.addEventListener('gestureend', prevent, { passive: false });
}

// Re-applies the theme whenever the route changes: the sign-in pages keep
// their own Light look, every other page has the one theme. Renders nothing;
// must sit inside <Router> for useLocation.
function PageThemeApplier() {
  const location = useLocation();
  useEffect(() => {
    applyThemeForPath(location.pathname);
  }, [location.pathname]);
  return null;
}

// Anonymous visit statistics for the PUBLIC pages only (sign in, verify,
// reset, join, confirm email); siteTracker refuses every other path, so the
// signed-in app is never tracked. Renders nothing.
function PublicPageviews() {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  // Inactivity-based session timeout. Hook always runs at the app
  // root (unconditional, satisfies rules of hooks). It reads the
  // configured timeout from localStorage (set by Workspace
  // Settings → Security → Session timeout); when no activity has
  // happened for that period it clears auth and bounces to /login.
  // Default = 'never' → the timer never arms, so this is a no-op
  // for orgs that haven't opted in.
  useSessionTimeout(() => {
    try { clearAuth(); } catch {}
    try { window.dispatchEvent(new Event('pidly:user-updated')); } catch {}
    if (typeof window !== 'undefined') {
      try { sessionStorage.setItem('pidly.signedOutByTimeout', '1'); } catch {}
      window.location.href = '/login';
    }
  });

  // App-wide interface-zoom shortcuts — pidly's own Ctrl +/- so it feels
  // native. The browser owns Ctrl+= / Ctrl+- / Ctrl+0, so we use Ctrl+Alt
  // (⌥ on Mac) to avoid clobbering either the browser or the PDF zoom.
  //   Ctrl+Alt+=  (or +)  zoom in
  //   Ctrl+Alt+-          zoom out
  //   Ctrl+Alt+0          reset to 100%
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || !e.altKey) return;
      const k = e.key;
      if (k === '=' || k === '+') { e.preventDefault(); stepUiZoom(+1); }
      else if (k === '-' || k === '_') { e.preventDefault(); stepUiZoom(-1); }
      else if (k === '0') { e.preventDefault(); resetUiZoom(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <ErrorBoundary>
    {/* Pending-approval interceptor — listens for the
        pidly:pending-approval event apiFetch dispatches whenever a
        backend response says the user's membership is pending.
        Renders a full-screen overlay; sits OUTSIDE Router so it can
        cover whatever route the user happens to be on. */}
    <PendingApprovalOverlay />
    {/* A project that requires two-factor sign-in: the code screen, from
        the same 403 path, outside Router for the same reason. */}
    <TwoFactorGate />
    {/* A newer build is live: offer the reload (an open tab never fetches new code by itself). */}
    <UpdateNotice />
    {/* A change that did not save is always said — whichever page made it. */}
    <SaveFailureNotice />
    <NoticeHost />
    <Router>
      {/* Per-page themes: re-resolve the effective theme on every route
          change (a page group pinned in Theme preferences wins over the
          main theme; see utils/theme.js pageGroupForPath). */}
      <PageThemeApplier />
      <PublicPageviews />
      {/* App-wide Theme picker — opened from any account menu via the
          pidly:open-theme event. Renders nothing until opened.
          INSIDE the Router (unlike PendingApprovalOverlay above): it
          needs useLocation to close itself when the user navigates, and
          Router does not unmount its children on a route change, so it
          is still a single app-wide instance. */}
      <ThemeDialogHost />
      <Suspense fallback={<PageLoading fullPage />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/verify" element={<VerifyEmailPage />} />
        <Route path="/confirm-email" element={<ConfirmEmailChangePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Workspace join landing — token in URL, public so people can preview */}
        <Route path="/join/:token" element={<JoinWorkspacePage />} />
        <Route path="/workspace/:orgId/wide" element={
          <ProtectedRoute>
            <ProjectWideWorkspace />
          </ProtectedRoute>
        } />
        {/* Landing page is the Overview — cross-workspace home with
            recent projects, tasks, and profile. The per-workspace
            Projects list lives at /projects. */}
        <Route path="/" element={<Navigate to="/overview" replace />} />

        {/* ONE element for all six shell paths, as a LAYOUT route.
            They were six sibling <Route>s each with their own
            <ProjectListPage /> — React Router treats those as different
            nodes, so moving between any two of them unmounted the page
            and mounted a fresh one. Every fetch it makes (projects,
            orgs, activity, tasks, recent docs) ran again and the dock
            visibly rebuilt itself, which is the "it reloads and it was
            already there".

            Safe because ProjectListPage derives its `view` from
            location.pathname DURING RENDER (see the ternary near the
            top of it), so a path change still switches the view — it
            just no longer throws the component away to do it. The
            children carry paths only; the page renders no <Outlet />,
            so nothing renders under it. */}
        <Route element={<ProtectedRoute><ProjectListPage /></ProtectedRoute>}>
          <Route path="/overview" />
          <Route path="/projects" />
          <Route path="/account" />
          <Route path="/admin" />
          <Route path="/my-tasks" />
          <Route path="/activity" />
          <Route path="/help" />
          <Route path="/help/:topic" />
          <Route path="/help/:topic/:article" />
          <Route path="/trash" />
          <Route path="/workspace/:orgId/settings" />
          <Route path="/workspace/:orgId/documents" />
        </Route>
        <Route path="/project/:projectId" element={
          <ProtectedRoute>
            <ProjectWorkspace />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/detection" element={
          <ProtectedRoute>
            <ProjectDetectionPage />
          </ProtectedRoute>
        } />
        {/* Legacy routes redirect to combined detection page */}
        <Route path="/project/:projectId/classes" element={
          <ProtectedRoute>
            <ProjectClassesPage />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/models" element={
          <ProtectedRoute>
            <ProjectModelsPage />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/smartlinks" element={
          <ProtectedRoute>
            <ProjectSmartLinksPage />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/docprops" element={
          <ProtectedRoute>
            <ProjectDocPropsPage />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/regions" element={
          <ProtectedRoute>
            <ProjectRegionsPage />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/extract" element={
          <ProtectedRoute>
            <ProjectExtractPage />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/symbols" element={
          <ProtectedRoute>
            <ProjectSymbolsPage />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/template/:templateId" element={
          <ProtectedRoute>
            <TemplateEditorPage />
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/twin" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoading fullPage />}>
              <ProjectVectorisationPage />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/project/:projectId/tasks" element={
          <ProtectedRoute>
            <ProjectPunchesPage />
          </ProtectedRoute>
        } />
        {/* Legacy alias — keep so any saved /punches links still resolve */}
        <Route path="/project/:projectId/punches" element={
          <ProtectedRoute>
            <ProjectPunchesPage />
          </ProtectedRoute>
        } />
      </Routes>
      </Suspense>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
